/**
 * Minimal IndexedDB key-value store for the offline cache. One database per signed-in user,
 * so switching the development identity never exposes another user's cached notes.
 * Everything here is best-effort: a browser without IndexedDB simply has no offline copies.
 */
const STORE = "kv";
const dbs = new Map<string, Promise<IDBDatabase | null>>();

export const cacheDbName = (userId: string) => `brainlog-claude-cache-${userId}`;

function open(userId: string): Promise<IDBDatabase | null> {
  const name = cacheDbName(userId);
  let p = dbs.get(name);
  if (!p) {
    p = new Promise((resolve) => {
      try {
        const req = indexedDB.open(name, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(STORE);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    dbs.set(name, p);
  }
  return p;
}

async function tx<T>(userId: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  const db = await open(userId);
  if (!db) return undefined;
  return new Promise((resolve) => {
    try {
      const t = db.transaction(STORE, mode);
      const req = fn(t.objectStore(STORE));
      t.oncomplete = () => resolve(req ? req.result : undefined);
      t.onerror = () => resolve(undefined);
      t.onabort = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
}

export const idbGet = <T>(userId: string, key: string) => tx<T>(userId, "readonly", (s) => s.get(key) as IDBRequest<T>);
export const idbSet = (userId: string, key: string, value: unknown) => tx(userId, "readwrite", (s) => void s.put(value, key));
export const idbDel = (userId: string, key: string) => tx(userId, "readwrite", (s) => void s.delete(key));
export const idbKeys = async (userId: string) => ((await tx<IDBValidKey[]>(userId, "readonly", (s) => s.getAllKeys())) ?? []).map(String);

/** Remove every offline copy for this user (plaintext Yjs caches and encrypted queues). */
export async function clearOfflineCache(userId: string) {
  const db = await open(userId);
  db?.close();
  dbs.delete(cacheDbName(userId));
  const names = (await indexedDB.databases?.().catch(() => [])) ?? [];
  const prefix = `brainlog-claude-note-${userId}-`;
  await Promise.all(
    [cacheDbName(userId), ...names.map((d) => d.name ?? "").filter((n) => n.startsWith(prefix))].map(
      (n) =>
        new Promise<void>((resolve) => {
          const req = indexedDB.deleteDatabase(n);
          req.onsuccess = req.onerror = req.onblocked = () => resolve();
        }),
    ),
  );
}
